import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  CardElement,
  useStripe,
  useElements,
  Elements,
} from "@stripe/react-stripe-js";
import {
  getStripePaymentMethods,
  updateCard,
  getAllPaymentMethods,
  createACHSetupIntent,
} from "../../api/paymentsAPI";
import { useSelector } from "react-redux";
import { selectMembershipDetails } from "../../store/slices/membershipSlice";
import { sendNewInvoiceCreateEmail } from "../../api/emailAPI";
import { useTheme } from "../../context/ThemeContext";
import { useTranslation } from "react-i18next";
import {
  getCardBackground,
  getPrimaryText,
  getSecondaryText,
  getInputStyles,
  getLightBorder,
  getPageBackground,
} from "../../utils/themeUtils";
import { getStripePromise } from "../../utils/stripeClient";
import StripeBlockedAlert from "./StripeBlockedAlert";
import { ACH_PROCESSING_MESSAGE } from "../../types/payment.types";
import BankAccountElement from "./stripe/BankAccountElement";
import FeeBreakdown from "./FeeBreakdown";
import { persistAuthToken } from "../../api/authTokenStore";
import { savedMethodsFrom } from "../../utils/savedPaymentMethods";
import { newAttemptKey } from "../../utils/attemptKey";
import { payTowardBalance } from "../../utils/payTowardBalance";

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPaymentSuccess?: () => void;
}

const PaymentModalForm: React.FC<PaymentModalProps> = ({ isOpen, onClose, onPaymentSuccess }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation('membership');
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [savedCards, setSavedCards] = useState<any[]>([]);
  const [savedBankAccounts, setSavedBankAccounts] = useState<any[]>([]);
  const [cardLoading, setCardLoading] = useState(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | "new" | "new_bank">("new");
  const [saveNewCard, setSaveNewCard] = useState(false);
  const [achClientSecret, setAchClientSecret] = useState("");
  const [loadingAchSetup, setLoadingAchSetup] = useState(false);

  const stripe = useStripe();
  const elements = useElements();
  const token = localStorage.getItem("authToken");
  const membership: any = useSelector(selectMembershipDetails);
  const storeClubName = localStorage.getItem("clubName");
  const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
  const stripeCustomerId = membership?.stripe_customer_id || "";

  // ONE payment attempt (utils/attemptKey): the same key on a double click or
  // on a retry after a dropped connection, so the server charges once; a new
  // key once the server has answered, or when the amount or card changes.
  const attemptKeyRef = useRef<string | null>(null);
  useEffect(() => {
    attemptKeyRef.current = null;
  }, [amount, selectedPaymentMethodId]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setAmount("");
      setSelectedPaymentMethodId(savedCards[0]?.id || savedBankAccounts[0]?.id || "new");
      setSaveNewCard(false);
      setAchClientSecret("");
    }
  }, [isOpen, savedCards, savedBankAccounts]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const numericAmount = parseFloat(amount);
    if (!numericAmount || numericAmount <= 0) {
      toast.error(t('payment.enterValidAmount', { defaultValue: 'Please enter a valid amount greater than 0.' }));
      return;
    }

    if (selectedPaymentMethodId === "new_bank") {
      toast.error(t('payment.linkBankFirst', { defaultValue: 'Please link your bank account first using the button below.' }));
      return;
    }

    setLoading(true);

    try {
      let paymentMethodId = selectedPaymentMethodId;
      let paymentType = "card";

      const selectedBankAccount = savedBankAccounts.find((ba) => ba.id === selectedPaymentMethodId);
      if (selectedBankAccount) {
        paymentType = "us_bank_account";
      }

      if (selectedPaymentMethodId === "new") {
        const cardElement = elements?.getElement(CardElement);
        if (!cardElement || !stripe) {
          toast.error(t('payment.stripeNotInitialized', { defaultValue: 'Stripe not initialized.' }));
          setLoading(false);
          return;
        }

        if (saveNewCard) {
          // Fetch a SetupIntent on demand so we don't run a Stripe call
          // on every modal-open or every selectedPaymentMethodId change.
          let setupClientSecret: string | undefined;
          let setupError: string | undefined;
          try {
            const setupRes = await updateCard(token, {
              stripeCustomerId,
              // With no card profile yet the server creates one from these
              // (400 without them); membershipId also routes a second
              // adult's card to their own profile.
              membershipId: membership?.id,
              email: membership?.email,
              firstName: membership?.first_name,
              lastName: membership?.last_name,
            });
            setupClientSecret = setupRes?.clientSecret;
            setupError = setupRes?.error;
          } catch {
            toast.error(t('payment.errorSavingCard', { defaultValue: 'Error saving card: ' }) + 'Could not initialize.');
            setLoading(false);
            return;
          }
          if (!setupClientSecret) {
            // Surface the backend's actual error — the API wrapper swallows
            // HTTP failures into { success:false, error }, and the old
            // hardcoded "No client secret." masked a 2-month member-pay
            // outage ("Forbidden - no role assigned") as a Stripe hiccup.
            toast.error(
              t('payment.errorSavingCard', { defaultValue: 'Error saving card: ' }) +
                (setupError || 'No client secret.')
            );
            setLoading(false);
            return;
          }

          const { setupIntent, error } = await stripe.confirmCardSetup(
            setupClientSecret,
            {
              payment_method: {
                card: cardElement,
              },
            }
          );

          if (error) {
            toast.error(t('payment.errorSavingCard', { defaultValue: 'Error saving card: ' }) + error.message);
            setLoading(false);
            return;
          }

          paymentMethodId = setupIntent.payment_method as string;
        } else {
          const { paymentMethod, error } = await stripe.createPaymentMethod({
            type: "card",
            card: cardElement,
          });

          if (error) {
            toast.error(t('payment.cardError', { defaultValue: 'Card error: ' }) + error.message);
            setLoading(false);
            return;
          }

          paymentMethodId = paymentMethod.id;
        }
      }

      if (!attemptKeyRef.current) attemptKeyRef.current = newAttemptKey();
      // Handles a card that needs the bank's verification (3-D Secure): this
      // window used to treat that answer as a failure, so such a card could
      // never pay here.
      const res = await payTowardBalance({
        token,
        stripe,
        onVerifying: () =>
          toast.info(t('payment.cardVerification', { defaultValue: 'Your card requires additional verification...' })),
        form: {
          amount: numericAmount,
          paymentMethodId,
          paymentType,
          saveCard: selectedPaymentMethodId === "new" && saveNewCard,
          // The account shown in the family switcher; the server checks the
          // signed-in person is on it before crediting it.
          membershipId: membership?.id || null,
          idempotencyKey: attemptKeyRef.current,
          processed_by: {
            userId: userDetails?.id,
            name: userDetails?.first_name + " " + userDetails?.last_name,
            role: userDetails?.role,
          },
        },
      });
      // The server answered (paid, refused or declined): the next click is a
      // new attempt. No answer at all keeps the key for the retry.
      if (res?.success || res?.answered !== false) attemptKeyRef.current = null;

      if (res?.success) {
        const card: any = savedCards.find((card) => card.id === selectedPaymentMethodId);
        const bankAccount: any = savedBankAccounts.find((ba) => ba.id === selectedPaymentMethodId);

        if (paymentType === "us_bank_account") {
          toast.success(t('payment.achInitiated', { defaultValue: 'ACH payment initiated! ' }) + ACH_PROCESSING_MESSAGE);
        } else {
          toast.success(t('payment.paymentSuccessful', { defaultValue: 'Payment successful!' }));
        }

        const payload = {
          invoiceId: res.data.invoiceId,
          memberId: res.data.memberId,
          clubName: storeClubName,
          message: paymentType === "us_bank_account"
            ? ` Advance payment initiated via bank account (${bankAccount?.us_bank_account?.bank_name} ****${bankAccount?.us_bank_account?.last4}).`
            : ` Advance payment charged by card (${card?.card?.last4}) and balance updated.`,
          cardsdetails: card?.card,
        };
        // A repeat of an attempt already recorded was not a new payment.
        if (!res.alreadyRecorded) await sendNewInvoiceCreateEmail(payload);
        setAmount("");
        onPaymentSuccess?.();
        onClose();
      } else {
        const { getPaymentErrorMessage } = await import("../../utils/paymentErrors");
        toast.error(getPaymentErrorMessage(res));
      }
    } catch (err: any) {
      const { getPaymentErrorMessage } = await import("../../utils/paymentErrors");
      toast.error(getPaymentErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // Fetch saved payment methods
  useEffect(() => {
    const fetchSavedPaymentMethods = async () => {
      if (!stripeCustomerId || !isOpen) return;

      setCardLoading(true);

      try {
        const response = await getAllPaymentMethods(token, stripeCustomerId);
        if (response?.success) {
          const allMethods = savedMethodsFrom(response);
          const cards = allMethods.filter((pm: any) => pm.type === 'card');
          const bankAccounts = allMethods.filter((pm: any) => pm.type === 'us_bank_account');

          setSavedCards(cards);
          setSavedBankAccounts(bankAccounts);

          // Auto-select first available payment method
          if (cards.length > 0) {
            setSelectedPaymentMethodId(cards[0].id);
          } else if (bankAccounts.length > 0) {
            setSelectedPaymentMethodId(bankAccounts[0].id);
          }

          if (response?.token) {
            persistAuthToken(response.token);
          }
        } else {
          const fallbackResponse = await getStripePaymentMethods(token, stripeCustomerId);
          if (fallbackResponse?.success) {
            setSavedCards(fallbackResponse?.data || []);
            setSavedBankAccounts([]);
            if (fallbackResponse?.data?.length > 0) {
              setSelectedPaymentMethodId(fallbackResponse.data[0].id);
            }
            if (fallbackResponse?.token) {
              persistAuthToken(fallbackResponse.token);
            }
          }
        }
      } catch (error) {
        // Error handled by axios interceptor (Sentry)
      } finally {
        setCardLoading(false);
      }
    };

    fetchSavedPaymentMethods();
  }, [stripeCustomerId, isOpen]);

  // Create ACH SetupIntent when "new bank account" is selected
  useEffect(() => {
    const createAchSetup = async () => {
      if (stripeCustomerId && selectedPaymentMethodId === "new_bank" && !achClientSecret) {
        setLoadingAchSetup(true);
        try {
          const response = await createACHSetupIntent(token, {
            stripeCustomerId: stripeCustomerId,
            membershipId: membership?.id,
          });
          if (response?.success && response?.clientSecret) {
            setAchClientSecret(response.clientSecret);
          } else {
            toast.error(t('payment.failedInitBankSetup', { defaultValue: 'Failed to initialize bank account setup' }));
          }
        } catch (err: any) {
          const errorMessage = err?.response?.data?.error || err?.message || t('payment.errorPreparingBankSetup', { defaultValue: 'Error preparing bank account setup' });
          toast.error(errorMessage);
        } finally {
          setLoadingAchSetup(false);
        }
      }
    };
    createAchSetup();
  }, [stripeCustomerId, selectedPaymentMethodId, membership?.id]);

  const handleBankAccountSuccess = async (paymentMethodId: string) => {
    setAchClientSecret("");
    setSelectedPaymentMethodId(paymentMethodId);
    try {
      const response = await getAllPaymentMethods(token, stripeCustomerId);
      if (response?.success) {
        const allMethods = savedMethodsFrom(response);
        setSavedCards(allMethods.filter((pm: any) => pm.type === 'card'));
        setSavedBankAccounts(allMethods.filter((pm: any) => pm.type === 'us_bank_account'));
      }
    } catch (err) {
      // Error handled by axios interceptor (Sentry)
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div
        className={`relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-lg shadow-xl ${getCardBackground(isDarkMode)}`}
      >
        {/* Header */}
        <div className={`sticky top-0 flex items-center justify-between p-4 border-b ${getLightBorder(isDarkMode)} ${getCardBackground(isDarkMode)}`}>
          <h2 className={`text-xl font-bold ${getPrimaryText(isDarkMode)}`}>
            {t('payment.makeAPayment', { defaultValue: 'Make a Payment' })}
          </h2>
          <button
            onClick={onClose}
            className={`p-1 rounded-full hover:bg-opacity-20 hover:bg-gray-500 transition-colors ${getPrimaryText(isDarkMode)}`}
            aria-label="Close modal"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Amount Input */}
          <label className="block">
            <span className={`block mb-2 font-medium ${getSecondaryText(isDarkMode)}`}>
              {t('payment.amount', { defaultValue: 'Amount' })}
            </span>
            <input
              type="number"
              step="0.01"
              min="0.01"
              placeholder="e.g. 50.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className={`mt-1 block w-full px-4 py-3 rounded-md shadow-sm focus:outline-none focus:ring-2 transition ${getInputStyles(isDarkMode)}`}
              style={{ borderColor: isDarkMode ? "#374151" : "#d1d5db" }}
              required
            />
          </label>

          {/* Payment Methods */}
          {cardLoading ? (
            <div className="flex justify-center items-center py-6">
              <div className="w-6 h-6 border-2 border-gray-400 dark:border-gray-500 border-t-transparent rounded-full animate-spin"></div>
              <span className={`ml-2 ${getSecondaryText(isDarkMode)}`}>
                {t('payment.loadingMethods', { defaultValue: 'Loading payment methods...' })}
              </span>
            </div>
          ) : (
            (savedCards.length > 0 || savedBankAccounts.length > 0) && (
              <div className={`p-3 rounded-lg border ${getLightBorder(isDarkMode)} ${getPageBackground(isDarkMode)}`}>
                <h3 className={`font-semibold mb-2 text-sm ${getPrimaryText(isDarkMode)}`}>
                  {t('payment.chooseMethod', { defaultValue: 'Choose a payment method' })}
                </h3>
                <div className="space-y-1">
                  {/* Saved Cards */}
                  {savedCards.length > 0 && (
                    <>
                      <p className={`text-xs font-medium uppercase tracking-wide mb-1 ${getSecondaryText(isDarkMode)}`}>
                        {t('payment.cards', { defaultValue: 'Cards' })}
                      </p>
                      {savedCards.map((card) => (
                        <label
                          key={card.id}
                          className={`flex items-center space-x-3 p-2 rounded-md cursor-pointer transition-colors text-sm ${
                            selectedPaymentMethodId === card.id
                              ? isDarkMode ? "bg-gray-700" : "bg-gray-100"
                              : isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-50"
                          }`}
                        >
                          <input
                            type="radio"
                            name="paymentMethod"
                            value={card.id}
                            checked={selectedPaymentMethodId === card.id}
                            onChange={() => setSelectedPaymentMethodId(card.id)}
                            className="w-4 h-4"
                          />
                          <span className={getSecondaryText(isDarkMode)}>
                            {card.card?.brand?.toUpperCase()} ****{card.card?.last4}
                          </span>
                        </label>
                      ))}
                    </>
                  )}

                  {/* Saved Bank Accounts */}
                  {savedBankAccounts.length > 0 && (
                    <>
                      <p className={`text-xs font-medium uppercase tracking-wide mb-1 mt-2 ${getSecondaryText(isDarkMode)}`}>
                        {t('payment.bankAccounts', { defaultValue: 'Bank Accounts' })}
                      </p>
                      {savedBankAccounts.map((bankAccount) => (
                        <label
                          key={bankAccount.id}
                          className={`flex items-center space-x-3 p-2 rounded-md cursor-pointer transition-colors text-sm ${
                            selectedPaymentMethodId === bankAccount.id
                              ? isDarkMode ? "bg-gray-700" : "bg-gray-100"
                              : isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-50"
                          }`}
                        >
                          <input
                            type="radio"
                            name="paymentMethod"
                            value={bankAccount.id}
                            checked={selectedPaymentMethodId === bankAccount.id}
                            onChange={() => setSelectedPaymentMethodId(bankAccount.id)}
                            className="w-4 h-4"
                          />
                          <span className={getSecondaryText(isDarkMode)}>
                            {bankAccount.us_bank_account?.bank_name} ****{bankAccount.us_bank_account?.last4}
                          </span>
                        </label>
                      ))}
                    </>
                  )}

                  <div className={`border-t ${getLightBorder(isDarkMode)} my-2`}></div>

                  {/* New Card Option */}
                  <label
                    className={`flex items-center space-x-3 p-2 rounded-md cursor-pointer transition-colors text-sm ${
                      selectedPaymentMethodId === "new"
                        ? isDarkMode ? "bg-gray-700" : "bg-gray-100"
                        : isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="new"
                      checked={selectedPaymentMethodId === "new"}
                      onChange={() => setSelectedPaymentMethodId("new")}
                      className="w-4 h-4"
                    />
                    <span className={getSecondaryText(isDarkMode)}>
                      {t('payment.useNewCard', { defaultValue: 'Use new card' })}
                    </span>
                  </label>

                  {/* New Bank Account Option */}
                  <label
                    className={`flex items-center space-x-3 p-2 rounded-md cursor-pointer transition-colors text-sm ${
                      selectedPaymentMethodId === "new_bank"
                        ? isDarkMode ? "bg-gray-700" : "bg-gray-100"
                        : isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="new_bank"
                      checked={selectedPaymentMethodId === "new_bank"}
                      onChange={() => setSelectedPaymentMethodId("new_bank")}
                      className="w-4 h-4"
                    />
                    <span className={getSecondaryText(isDarkMode)}>
                      {t('payment.linkNewBank', { defaultValue: 'Link new bank account (ACH)' })}
                    </span>
                  </label>
                </div>
              </div>
            )
          )}

          {/* ACH Processing Notice */}
          {savedBankAccounts.find((ba) => ba.id === selectedPaymentMethodId) && (
            <div className={`p-2 rounded-lg text-xs ${isDarkMode ? 'bg-yellow-900/30 text-yellow-200' : 'bg-yellow-50 text-yellow-800'} border ${isDarkMode ? 'border-yellow-700' : 'border-yellow-200'}`}>
              <strong>{t('common.note', { defaultValue: 'Note' })}:</strong> {ACH_PROCESSING_MESSAGE}
            </div>
          )}

          {/* New Card Form */}
          {selectedPaymentMethodId === "new" && (
            <div className="space-y-3">
              <div
                className={`p-3 border rounded-md ${
                  isDarkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-300"
                }`}
              >
                <CardElement
                  options={{
                    style: {
                      base: {
                        fontSize: "16px",
                        color: isDarkMode ? "#f3f4f6" : "#1f2937",
                        "::placeholder": { color: isDarkMode ? "#9ca3af" : "#6b7280" },
                      },
                      invalid: { color: "#ef4444" },
                    },
                  }}
                />
              </div>
              <label className={`flex items-center space-x-2 cursor-pointer text-sm ${getSecondaryText(isDarkMode)}`}>
                <input
                  type="checkbox"
                  checked={saveNewCard}
                  onChange={(e) => setSaveNewCard(e.target.checked)}
                  className="w-4 h-4"
                />
                <span>{t('payment.saveCard', { defaultValue: 'Save this card for future payments' })}</span>
              </label>
            </div>
          )}

          {/* New Bank Account Form */}
          {selectedPaymentMethodId === "new_bank" && (
            <div className="space-y-3">
              {loadingAchSetup ? (
                <div className="flex justify-center items-center py-6">
                  <div className="w-6 h-6 border-2 border-gray-400 dark:border-gray-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className={`ml-2 text-sm ${getSecondaryText(isDarkMode)}`}>
                    {t('payment.initBankSetup', { defaultValue: 'Initializing bank account setup...' })}
                  </span>
                </div>
              ) : achClientSecret ? (
                <BankAccountElement
                  clientSecret={achClientSecret}
                  stripeCustomerId={stripeCustomerId}
                  memberName={`${membership?.first_name || ''} ${membership?.last_name || ''}`}
                  onSuccess={handleBankAccountSuccess}
                  onCancel={() => {
                    setSelectedPaymentMethodId(savedCards[0]?.id || savedBankAccounts[0]?.id || "new");
                    setAchClientSecret("");
                  }}
                />
              ) : (
                <div className={`p-3 border rounded-lg ${getLightBorder(isDarkMode)} ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                  <p className={`text-sm ${getSecondaryText(isDarkMode)}`}>
                    {t('payment.clickToInitBank', { defaultValue: 'Click to initialize bank account linking...' })}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Fee Breakdown */}
          {parseFloat(amount) > 0 && (
            <FeeBreakdown
              amount={parseFloat(amount)}
              paymentType={savedBankAccounts.find((ba) => ba.id === selectedPaymentMethodId) ? "ach" : "card"}
            />
          )}

          {/* Submit Button */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[var(--cc-action)] text-[var(--cc-action-text)] hover:bg-[var(--cc-action-hover)] py-3 px-6 rounded-md font-semibold disabled:opacity-50 transition-colors"
            >
              {loading ? t('payment.processing', { defaultValue: 'Processing...' }) : t('billing.payNow')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Wrapper to load Stripe Elements
const PaymentModal: React.FC<PaymentModalProps> = (props) => {
  if (!props.isOpen) return null;

  return (
    <Elements stripe={getStripePromise()}>
      <StripeBlockedAlert />
      <PaymentModalForm {...props} />
    </Elements>
  );
};

export default PaymentModal;
